import React, { useEffect, useRef, useState } from "react";
import API_ENDPOINTS from "../../shared/api-endpoints";

function getFilesSize(files = []) {
  return files.reduce((sum, file) => sum + (file.size || 0), 0);
}

function getExtension(name) {
  const ext = `.${String(name || "").split(".").pop().toLowerCase()}`;
  return ext === "." ? "" : ext;
}

export default function ComplaintUploadField({
  fieldId,
  title,
  currentFiles,
  allowedExtensions,
  fileSizeLimit,
  limitOfFileNumber,
  messages,
  onUploadSuccess,
  onRemoveSuccess,
}) {
  const inputRef = useRef(null);
  const widgetRef = useRef(null);
  const currentFilesRef = useRef(currentFiles);
  const uploadFilesSumRef = useRef(getFilesSize(currentFiles));
  const onUploadSuccessRef = useRef(onUploadSuccess);
  const onRemoveSuccessRef = useRef(onRemoveSuccess);
  const [uploadError, setUploadError] = useState("");

  useEffect(() => {
    currentFilesRef.current = currentFiles;
    uploadFilesSumRef.current = getFilesSize(currentFiles);
  }, [currentFiles]);

  useEffect(() => {
    onUploadSuccessRef.current = onUploadSuccess;
  }, [onUploadSuccess]);

  useEffect(() => {
    onRemoveSuccessRef.current = onRemoveSuccess;
  }, [onRemoveSuccess]);

  useEffect(() => {
    if (!inputRef.current || !window.$?.fn?.kendoUpload) {
      return undefined;
    }

    const $input = window.$(inputRef.current);

    function onSelect(event) {
      setUploadError("");

      window.$.each(event.files, (index, file) => {
        const invalidExtension =
          getExtension(file.name) && !allowedExtensions.includes(getExtension(file.name));
        const invalidMaxSize = file.size > fileSizeLimit;
        const overTotal =
          uploadFilesSumRef.current > fileSizeLimit ||
          file.size > fileSizeLimit ||
          uploadFilesSumRef.current + file.size > fileSizeLimit;
        const overCount =
          window.$(".k-file[data-uid]").length > limitOfFileNumber - 1 ||
          window.$(".k-file[data-uid]").length + index > limitOfFileNumber - 1;

        if (invalidExtension) {
          event.files[index].validationErrors = ["invalidFileExtension"];
          setUploadError(messages.invalidFileExtensionText);
          return;
        }

        if (invalidMaxSize || overTotal || overCount) {
          event.files[index].validationErrors = [
            invalidMaxSize ? "invalidMaxFileSize" : "filesSumSizeLimit",
          ];
          setUploadError(messages.invalidMaxFileSizeText);
          const uid = event.files[index].uid;
          const entry = window.$(`.k-file[data-uid='${uid}']`);

          if (entry.length > 0) {
            entry.remove();
          }

          return;
        }

        if (!event.files[index].validationErrors) {
          uploadFilesSumRef.current += file.size;
        }
      });
    }

    function onSuccess(event) {
      if (event.operation === "remove") {
        return;
      }

      setUploadError("");
      const file = event.files[0];
      onUploadSuccessRef.current({
        id: fieldId,
        name: file.name,
        nameGuid: event.response,
        size: file.size,
      });
    }

    function onRemove(event) {
      setUploadError("");

      const file = event.files[0];
      onRemoveSuccessRef.current(file.nameGuid);

      if (uploadFilesSumRef.current >= (file.size || 0) && !file.validationErrors) {
        uploadFilesSumRef.current -= file.size || 0;
      }

      event.data = {
        nameGuid: file.nameGuid,
      };
    }

    function onError(event) {
      if (event.XMLHttpRequest?.status === 413) {
        setUploadError(messages.invalidMaxFileSizeText);
      } else if (event.XMLHttpRequest?.status === 415) {
        setUploadError(messages.invalidFileExtensionText);
      }

      const files = event.files || [];

      for (let index = 0; index < files.length; index += 1) {
        const entry = window.$(`.k-file[data-uid='${files[index].uid}']`);
        if (entry.length > 0) {
          entry.remove();
        }
      }
    }

    $input.kendoUpload({
      async: {
        saveUrl: API_ENDPOINTS.uploadSave,
        removeUrl: API_ENDPOINTS.uploadRemove,
      },
      localization: {
        invalidFileExtension: messages.invalidFileExtensionHtml,
        invalidMaxFileSize: messages.invalidMaxFileSizeHtml,
        filesSumSizeLimit: messages.invalidMaxFileSizeHtml,
        dropFilesHere: messages.dropFilesHere,
        select: messages.select,
      },
      select: onSelect,
      success: onSuccess,
      remove: onRemove,
      validation: {
        allowedExtensions,
        maxFileSize: fileSizeLimit,
      },
      error: onError,
    });

    widgetRef.current = $input.data("kendoUpload");

    return () => {
      if (widgetRef.current?.destroy) {
        widgetRef.current.destroy();
      }

      widgetRef.current = null;
    };
  }, [allowedExtensions, fieldId, fileSizeLimit, limitOfFileNumber, messages]);

  return (
    <div className="col-md-8">
      <p className="mt-5 mb-1 text-corporateBlue">{title}</p>
      <input ref={inputRef} type="file" id={fieldId} />
      {uploadError ? (
        <div className="text-danger mt-2">
          <i>{uploadError}</i>
        </div>
      ) : null}
    </div>
  );
}
